import { useRouter } from 'vue-router';
import { useApolloClient } from '@vue/apollo-composable';
import { logoutWithCookies } from '@dott/core';
import { useAuthStore } from '@/stores/auth';
import { clearAppBadge } from '@/shared/composables/useAppBadge';
import { getLogoutUrl } from '../tools/auth';

export const useLogout = () => {
  const router = useRouter();
  const authStore = useAuthStore();
  const { client } = useApolloClient();

  const logout = async (): Promise<void> => {
    // Mute the cross-tab SESSION_LOGGED_OUT handler on this tab — the server
    // publishes the event during logoutWithCookies and we'd otherwise race
    // with our own teardown (and crash MainLayout via its $reset).
    authStore.isLoggingOut = true;

    try {
      await logoutWithCookies(getLogoutUrl());

      // clearStore aborts in-flight queries with an "Invariant Violation",
      // which is benign in this context — we're tearing it all down anyway.
      try {
        await client.clearStore();
      } catch {
        // ignore
      }

      // Refetch instead of nulling user.value: MainLayout is still mounted
      // until the route transition to /login completes, and its template
      // dereferences currentUser. With the session cookie gone, the API
      // returns Guest, which the auth middleware accepts on /login.
      await authStore.fetchMe();

      // The app-icon badge is origin-wide and outlives the page — drop it with
      // the session so the next user doesn't inherit this one's unread count.
      clearAppBadge();

      await router.replace({ name: 'Login' });
    } finally {
      authStore.isLoggingOut = false;
    }
  };

  return {
    logout,
  };
};
